import { useMemo, useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp, Check, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { describeAiStatus, type SkipDecision, type SkipPlan } from '../types/skipPlan';
import type { AcceptedAnswer } from '../hooks/useSkipPlan';

interface SkipPlanBannerProps {
  plan: SkipPlan | null;
  /** questionId → question text, for display */
  questionText?: Record<string, string>;
  onAccept: (accepted: AcceptedAnswer[]) => Promise<boolean> | boolean | void;
  applying?: boolean;
  className?: string;
}

function formatAnswer(answer: unknown): string {
  if (answer === null || answer === undefined) return '';
  if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
  if (Array.isArray(answer)) return answer.map(String).join(', ');
  if (typeof answer === 'object') return JSON.stringify(answer);
  return String(answer);
}

export function toAcceptedAnswers(decisions: ReadonlyArray<SkipDecision>): AcceptedAnswer[] {
  return decisions
    .filter((d) => d.answer !== undefined)
    .map((d) => ({ questionId: d.questionId, answer: d.answer, confidence: d.confidence, evidence: d.evidence, action: d.action === 'skip' ? 'skip' : 'suggest' }));
}

/**
 * Shows what the AI pre-filled from the engagement's documents and lets the
 * user accept it. Rules-based skips (already answered / not applicable) are not
 * listed; they need no decision. When the model did not run, the banner
 * explains why in one line and otherwise stays out of the way.
 */
export function SkipPlanBanner({ plan, questionText = {}, onAccept, applying = false, className = '' }: SkipPlanBannerProps) {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());

  const inferred = useMemo(
    () => (plan ? plan.decisions.filter((d) => d.decidedBy === 'ai' && (d.action === 'skip' || d.action === 'suggest') && d.answer !== undefined && !accepted.has(d.questionId)) : []),
    [plan, accepted],
  );

  if (!plan) return null;

  const note = describeAiStatus(plan.aiStatus, plan.aiDetail);

  if (inferred.length === 0) {
    if (!note) return null;
    return (
      <div className={`flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 ${className}`} data-testid="skip-plan-note">
        <Sparkles className="size-3.5 text-slate-400" aria-hidden="true" />
        <span>{note}</span>
      </div>
    );
  }

  const accept = async (decisions: SkipDecision[]) => {
    const payload = toAcceptedAnswers(decisions);
    const ok = await onAccept(payload);
    if (ok !== false) {
      setAccepted((prev) => {
        const next = new Set(prev);
        payload.forEach((a) => next.add(a.questionId));
        return next;
      });
    }
  };

  const fromDocs = inferred.filter((d) => d.action === 'skip').length;
  const suggested = inferred.length - fromDocs;

  return (
    <div className={`rounded-lg border border-indigo-200 bg-indigo-50/60 ${className}`} data-testid="skip-plan-banner">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="flex items-start gap-2 min-w-0">
          <Sparkles className="size-4 text-indigo-600 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm text-indigo-950">
              {fromDocs > 0 && <span>{fromDocs} {fromDocs === 1 ? 'question is' : 'questions are'} answered in your documents</span>}
              {fromDocs > 0 && suggested > 0 && <span>, and </span>}
              {suggested > 0 && <span>{suggested} {suggested === 1 ? 'has' : 'have'} a suggested answer</span>}
              <span>. Review and accept to skip them.</span>
            </p>
            {plan.contextSources.length > 0 && (
              <p className="text-xs text-indigo-700/80 mt-0.5">Based on: {plan.contextSources.join(', ')}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            <span className="ml-1">{open ? 'Hide' : 'Review'}</span>
          </Button>
          <Button size="sm" className="h-8" disabled={applying} onClick={() => accept(inferred)} data-testid="skip-plan-accept-all">
            <Check className="size-4 mr-1" />
            Accept all ({inferred.length})
          </Button>
        </div>
      </div>

      {open && (
        <ul className="divide-y divide-indigo-100 border-t border-indigo-100">
          {inferred.map((d) => (
            <li key={d.questionId} className="px-4 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-slate-900">{questionText[d.questionId] ?? d.questionId}</p>
                <p className="text-sm text-slate-700 mt-0.5">
                  <span className="text-slate-500">Answer: </span>
                  {formatAnswer(d.answer)}
                </p>
                {d.evidence?.excerpt && (
                  <p className="text-xs text-slate-500 mt-1 flex items-start gap-1">
                    <Quote className="size-3 mt-0.5 shrink-0" aria-hidden="true" />
                    <span className="italic">“{d.evidence.excerpt}”</span>
                    <span className="not-italic">({d.evidence.source})</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={d.action === 'skip' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                  {d.action === 'skip' ? 'In documents' : 'Suggested'} · {Math.round(d.confidence * 100)}%
                </Badge>
                <Button size="sm" variant="outline" className="h-7" disabled={applying} onClick={() => accept([d])}>
                  Use
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
